import Hls from "hls.js";

import { playerSignatureFragLoader } from "../hls_player_signature";

let sharedPreviewAudioEnabled = false;

export const ThumbnailPreviewVideo = {
  mounted() {
    this.hls = null;
    this.previewVideo = null;
    this.previewThumbnailEndPlayButtonOverlay = null;
    this.hovering = false;
    this.previewAudioEnabled = sharedPreviewAudioEnabled;

    this.thumbnailLink = this.el.matches("[data-hls-url]")
      ? this.el
      : this.el.querySelector("[data-hls-url]");
    if (!this.thumbnailLink) return;
    this.previewHideElements = this.thumbnailLink.querySelectorAll(
      "[data-preview-hide]",
    );
    this.audioToggleButton = this.thumbnailLink.querySelector(
      "[data-audio-toggle]",
    );
    this.audioMutedIcon = this.thumbnailLink.querySelector(
      "[data-audio-muted-icon]",
    );
    this.audioUnmutedIcon = this.thumbnailLink.querySelector(
      "[data-audio-unmuted-icon]",
    );

    // this also doesn't make sense on mobile.
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (isMobile) {
      return;
    }

    this.onMouseEnter = this.handleMouseEnter.bind(this);
    this.onMouseLeave = this.handleMouseLeave.bind(this);
    this.thumbnailLink.addEventListener("mouseenter", this.onMouseEnter);
    this.thumbnailLink.addEventListener("mouseleave", this.onMouseLeave);

    this.onAudioToggleClick = this.handleAudioToggleClick.bind(this);
    if (this.audioToggleButton) {
      this.audioToggleButton.addEventListener("click", this.onAudioToggleClick);
    }
  },

  handleMouseEnter() {
    const hlsUrl = this.thumbnailLink.dataset.hlsUrl;
    if (!hlsUrl || this.hovering) return;

    this.previewAudioEnabled = sharedPreviewAudioEnabled;
    this.hovering = true;
    this.el.setAttribute("phx-update", "ignore");

    const startTime = parseFloat(this.thumbnailLink.dataset.start || "0")
    const subtitleUrl = this.thumbnailLink.dataset.subtitleUrl;
    const subtitleSrclang = this.thumbnailLink.dataset.subtitleSrclang;

    const video = document.createElement("video");
    video.muted = !this.previewAudioEnabled;
    video.playsInline = true;
    video.loop = true;
    video.style.cssText = `
      position: absolute;
      top: 0; left: 0;
      width: 100%; height: 100%;
      object-fit: cover;
      opacity: 0;
      transition: opacity 0.25s ease;
      z-index: 0;
      pointer-events: none;
    `;

    // overflow: hidden clips the video to the link's rounded corners
    this.thumbnailLink.style.overflow = "hidden";
    this.thumbnailLink.insertAdjacentElement("afterbegin", video);
    this.previewVideo = video;
    this.setAudioMutedState(video.muted);

    if (subtitleUrl && subtitleSrclang) {
      this.setupSubtitles(video, subtitleUrl, subtitleSrclang);
    }

    const showVideo = () => {
      if (this.previewVideo === video && !this.previewThumbnailEndPlayButtonOverlay) {
        video.style.opacity = "1";
        this.setPreviewHideVisibility(false);
        this.setAudioToggleVisibility(true);
      }
    };

    if (Hls.isSupported()) {
      this.hls = new Hls({
        startPosition: startTime,
        startLevel: -1,
        maxBufferLength: 12,
        maxMaxBufferLength: 12,
        enableWorker: false,
        fLoader: playerSignatureFragLoader,
      });
      this.hls.loadSource(hlsUrl);
      this.hls.attachMedia(video);
      this.hls.on(Hls.Events.ERROR, (_event, data) => {
        const responseCode = Number(
          data?.response?.code ??
            data?.response?.status ??
            data?.response?.statusCode,
        );
        const isUnauthorized = responseCode === 401 || responseCode === 403;
        if (!isUnauthorized || !this.hovering || this.previewThumbnailEndPlayButtonOverlay) return;
        this.hls.stopLoad();
        this.showPreviewThumbnailEndPlayButton();
      });
      this.hls.on(Hls.Events.MANIFEST_PARSED, () => {
        if (!this.hovering) return;
        video.play().catch(() => {});
      });
      video.addEventListener("canplay", showVideo, { once: true });
      video.addEventListener("timeupdate", showVideo, { once: true });
    } else {
      //don't support safari <17.1
      return;
    }
  },

  handleAudioToggleClick(event) {
    event.preventDefault();
    event.stopPropagation();

    const video = this.previewVideo;
    if (!video || !this.hovering) return;

    video.muted = !video.muted;
    this.previewAudioEnabled = !video.muted;
    sharedPreviewAudioEnabled = this.previewAudioEnabled;
    this.setAudioMutedState(video.muted);
  },

  showPreviewThumbnailEndPlayButton() {
    if (this.previewThumbnailEndPlayButtonOverlay) return;
    if (this.previewVideo) {
      try {
        this.previewVideo.pause();
      } catch (_) {}
    }
    this.previewThumbnailEndPlayButtonOverlay = document.createElement("div");
    this.previewThumbnailEndPlayButtonOverlay.style.cssText = `
      position: absolute;
      inset: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 20;
      pointer-events: none;
    `;
    const playCircle = document.createElement("div");
    playCircle.style.cssText = `
      width: 4rem;
      height: 4rem;
      display: flex;
      align-items: center;
      justify-content: center;
      padding-left: 0.25rem;
      color: white;
      background: rgba(0, 0, 0, 0.2);
      backdrop-filter: blur(24px);
      -webkit-backdrop-filter: blur(24px);
      border-radius: 9999px;
    `;
    const playSvg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    playSvg.setAttribute("viewBox", "0 0 24 24");
    playSvg.setAttribute("fill", "currentColor");
    playSvg.setAttribute("width", "2rem");
    playSvg.setAttribute("height", "2rem");
    playSvg.innerHTML =
      '<path d="M8 5v14l11-7z"/>';
    playCircle.appendChild(playSvg);
    this.previewThumbnailEndPlayButtonOverlay.appendChild(playCircle);
    this.thumbnailLink.appendChild(this.previewThumbnailEndPlayButtonOverlay);
    this.setPreviewHideVisibility(false);
    this.setAudioToggleVisibility(false);
  },

  removePreviewThumbnailEndPlayButtonOverlay() {
    if (!this.previewThumbnailEndPlayButtonOverlay) return;
    this.previewThumbnailEndPlayButtonOverlay.remove();
    this.previewThumbnailEndPlayButtonOverlay = null;
  },

  setAudioToggleVisibility(visible) {
    if (!this.audioToggleButton) return;

    if (visible) {
      this.audioToggleButton.style.opacity = "1";
      this.audioToggleButton.style.pointerEvents = "auto";
      return;
    }

    this.audioToggleButton.style.opacity = "0";
    this.audioToggleButton.style.pointerEvents = "none";
  },

  setPreviewHideVisibility(visible) {
    if (!this.previewHideElements || this.previewHideElements.length === 0)
      return;

    for (const element of this.previewHideElements) {
      element.style.opacity = visible ? "1" : "0";
    }
  },

  setAudioMutedState(isMuted) {
    if (!this.audioToggleButton) return;

    this.audioToggleButton.setAttribute(
      "aria-pressed",
      isMuted ? "false" : "true",
    );
    if (this.audioMutedIcon) {
      this.audioMutedIcon.classList.toggle("hidden", !isMuted);
    }
    if (this.audioUnmutedIcon) {
      this.audioUnmutedIcon.classList.toggle("hidden", isMuted);
    }
  },

  setupSubtitles(video, subtitleUrl, srclang) {
    // Overlay element sits above the video but below other UI chrome
    const overlay = document.createElement("div");
    overlay.style.cssText = `
      position: absolute;
      bottom: 0.3rem;
      left: 0;
      width: 100%;
      text-align: center;
      pointer-events: none;
      z-index: 5;
      padding: 0 0.25rem;
      margin-bottom: 0.54rem;
    `;

    const subtitleSpan = document.createElement("span");
    subtitleSpan.style.cssText = `
      display: none;
      background: rgba(0, 0, 0, 1);
      color: white;
      font-size: 1rem;
      font-weight: 500;
      line-height: 1.35;
      padding: 1px 5px 3px;
      border-radius: 0.25rem;
      max-width: 95%;
      word-wrap: break-word;
    `;

    overlay.appendChild(subtitleSpan);
    this.thumbnailLink.appendChild(overlay);
    this.subtitleOverlay = overlay;

    const track = document.createElement("track");
    track.kind = "subtitles";
    track.src = subtitleUrl;
    track.srclang = srclang;
    video.appendChild(track);

    // "hidden" loads cues and fires cuechange but suppresses native rendering
    const textTrack = track.track;
    if (textTrack) {
      textTrack.mode = "hidden";
      textTrack.addEventListener("cuechange", () => {
        const cues = textTrack.activeCues;
        if (cues && cues.length > 0) {
          subtitleSpan.innerHTML = cues[0].text.replace(/\n/g, "<br>");
          subtitleSpan.style.display = "inline";
        } else {
          subtitleSpan.style.display = "none";
        }
      });
    }
  },

  handleMouseLeave() {
    if (!this.hovering) return;
    this.hovering = false;

    const video = this.previewVideo;
    this.previewVideo = null;

    if (video) {
      video.style.opacity = "0";
      setTimeout(() => {
        video.pause();
        video.remove();
      }, 250);
    }

    if (this.hls) {
      const hls = this.hls;
      this.hls = null;
      setTimeout(() => hls.destroy(), 250);
    }

    if (this.subtitleOverlay) {
      this.subtitleOverlay.remove();
      this.subtitleOverlay = null;
    }

    this.removePreviewThumbnailEndPlayButtonOverlay();
    this.setPreviewHideVisibility(true);
    this.setAudioMutedState(!this.previewAudioEnabled);
    this.setAudioToggleVisibility(false);
    this.thumbnailLink.style.overflow = "";
    this.el.removeAttribute("phx-update");
  },

  destroyed() {
    if (this.thumbnailLink) {
      this.thumbnailLink.removeEventListener("mouseenter", this.onMouseEnter);
      this.thumbnailLink.removeEventListener("mouseleave", this.onMouseLeave);
    }
    if (this.audioToggleButton) {
      this.audioToggleButton.removeEventListener(
        "click",
        this.onAudioToggleClick,
      );
    }
    if (this.hls) {
      this.hls.destroy();
      this.hls = null;
    }
    if (this.previewVideo) {
      this.previewVideo.remove();
      this.previewVideo = null;
    }
    if (this.subtitleOverlay) {
      this.subtitleOverlay.remove();
      this.subtitleOverlay = null;
    }
    this.removePreviewThumbnailEndPlayButtonOverlay();
  },
};
